import { computed, type Ref, ref } from 'vue'

type UseSelectionParams<T> = {
  initialSelection: Array<T>;
  items: Array<T>;
}

type UseSelectionReturn<T> = {
  selectedItems: Ref<Array<T>>;
  isItemSelected: (item: T) => boolean;
  areAllItemsSelected: Ref<boolean>;
  areSomeItemsSelected: Ref<boolean>;
  toggleItemSelection: (item: T) => void;
  selectAllItems: () => void;
  unselectAllItems: () => void;
}

export function useSelection<T extends { id: number }> ({
  initialSelection,
  items,
}: UseSelectionParams<T>): UseSelectionReturn<T> {
  const selectedItems: Ref<Array<T>> = ref(initialSelection.map(item => item)) as Ref<Array<T>>
  const areAllItemsSelected = computed(() =>
    selectedItems.value.length > 0 && selectedItems.value.length === items.length,
  )

  const areSomeItemsSelected = computed(() =>
    selectedItems.value.length > 0 && selectedItems.value.length < items.length,
  )

  const toggleItemSelection = (item: T) => {
    if (isItemSelected(item)) {
      selectedItems.value = selectedItems.value.filter((selectedItem: T) => selectedItem.id !== item.id)
    } else {
      selectedItems.value.push(item)
    }
  }

  const isItemSelected = (item: T): boolean => {
    // Can't use strict identity checking because some components have copies
    return selectedItems.value.some((selectedItem: T) => selectedItem.id === item.id)
  }

  const selectAllItems = () => {
    selectedItems.value = items
  }

  const unselectAllItems = () => {
    selectedItems.value = []
  }

  return {
    selectedItems,
    isItemSelected,
    areAllItemsSelected,
    areSomeItemsSelected,
    toggleItemSelection,
    selectAllItems,
    unselectAllItems,
  }
}
